import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import GameBoard from "./components/GameBoard";
import { Fab, Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { PlayArrow } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import logging from "utils/logging";
import {
  selectGameId,
  selectGameStatusObject,
} from "../gamestate/gameStateSlice";
import {
  GAMESTATE_CREDITS,
  GAMESTATE_LOBBY,
  GAMESTATE_PREGAME,
  GAMESTATE_INSTRUCTIONS,
  GAMESTATE_ROUND,
  GAMESTATE_ROUND_FINAL,
  GAMESTATE_ROUNDSTATE_ROUND_INTRO,
  GAMESTATE_ROUNDSTATE_QUESTION_ANSWERING,
  GAMESTATE_ROUNDSTATE_QUESTION_JUDGING,
  GAMESTATE_ROUNDSTATE_QUESTION_READING,
  GAMESTATE_ROUNDSTATE_QUESTION_RESULTS,
  GAMESTATE_ROUNDSTATE_QUESTION_REVEALING,
  GAMESTATE_ROUNDSTATE_FINAL_REVEALING,
  GAMESTATE_ROUNDSTATE_FINALROUND_QUESTION_INTRO,
} from "../gamestate/constants";
import Lobby from "../lobby/Lobby";
import InterRoundLobby from "./components/InterRoundLobby";
import PregameScreen from "../pregame/PregameScreen";
import GameboardLeaderboard from "./components/GameBoardLeaderboard";
import { FinalRoundBoard } from "./components/FinalRoundBoard";
import { FinalRoundPodiums } from "./components/FinalRoundPodiums";
import { GameBoardModal } from "./components/GameBoardModal";
import { FinalCredits } from "./components/FinalCredits";
import { updateGameStateAction } from "../gamestate/stateActions";
import * as Constants from "../gamestate/constants";
import Provider from "../../provider";

import InstructionsScreen from "../host/components/InstructionsScreen";
import RoundIntroScreen from "./components/RoundIntroScreen";
import BackgroundEffect from "features/components/BackgroundEffect";
import Analytics from "analytics";
import { EVENTS } from "analytics/events";

const log = logging.getLogger("GameBoardState");

const Root = styled('div')(({ theme }) => ({
  height: "100%",
  padding: theme.spacing(0.1),
}));

const StyledFab = styled(Fab)(({ theme }) => ({
  position: "fixed",
  right: theme.spacing(4),
  bottom: theme.spacing(4),
  backgroundColor: "#2ec4b6",
  "&:hover": {
    backgroundColor: "#26a89c",
  },
}));

const FabIcon = styled(PlayArrow)(({ theme }) => ({
  marginRight: theme.spacing(1),
}));

const GameBoardState = () => {
  const dispatch = useDispatch();

  const gameStatus = useSelector(selectGameStatusObject);
  const gameId = useSelector(selectGameId);

  useEffect(() => {
    document.body.style.backgroundColor = "black";
    if (gameId) {
      Analytics.track(EVENTS.GAMEBOARD_OPENED, {
        game_id: gameId,
        viewport_width: window.innerWidth,
        viewport_height: window.innerHeight,
        is_second_screen_likely: window.innerWidth >= 1920,
        current_state: gameStatus.currentState,
        current_round: gameStatus.round,
      });
    }
  }, [gameId]);

  // Keep the screen awake while the gameboard is active
  useEffect(() => {
    const wakeLockRef = { current: null };

    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator) {
          if (wakeLockRef.current) {
            await wakeLockRef.current.release();
          }
          wakeLockRef.current = await navigator.wakeLock.request('screen');
        }
      } catch (err) {
        log.warn("Wake lock request failed — screen may turn off:", err.message);
      }
    };

    requestWakeLock();

    // Re-acquire wake lock when tab becomes visible again (it auto-releases on hide)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        requestWakeLock();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch(() => {});
      }
    };
  }, []);

  if (gameStatus.currentState === GAMESTATE_LOBBY) {
    // Check if this is inter-round mode (gameStatus.round exists) or new game mode (no round)
    if (gameStatus.round) {
      return <InterRoundLobby />;
    }
    return <Lobby />;
  }

  if (gameStatus.currentState === GAMESTATE_PREGAME) {
    return <PregameScreen />;
  }

  if (gameStatus.currentState === GAMESTATE_INSTRUCTIONS) {
    return <InstructionsScreen />;
  }

  if (
    gameStatus.currentState === GAMESTATE_ROUND &&
    gameStatus.roundState === GAMESTATE_ROUNDSTATE_ROUND_INTRO
  ) {
    return <RoundIntroScreen />;
  }

  if (
    gameStatus.round &&
    gameStatus.round === GAMESTATE_ROUND_FINAL &&
    (gameStatus.roundState === GAMESTATE_ROUNDSTATE_QUESTION_REVEALING ||
     gameStatus.roundState === GAMESTATE_ROUNDSTATE_FINAL_REVEALING)
  ) {
    return <FinalRoundPodiums />;
  }

  if (gameStatus.currentState === GAMESTATE_CREDITS) {
    return <FinalCredits />;
  }

  let mainBoard = <GameBoard />;
  if (gameStatus.round === GAMESTATE_ROUND_FINAL) {
    mainBoard = <FinalRoundBoard />;
  }

  let questionVisibleState = [
    GAMESTATE_ROUNDSTATE_FINALROUND_QUESTION_INTRO,
    GAMESTATE_ROUNDSTATE_QUESTION_READING,
    GAMESTATE_ROUNDSTATE_QUESTION_ANSWERING,
    GAMESTATE_ROUNDSTATE_QUESTION_JUDGING,
    GAMESTATE_ROUNDSTATE_QUESTION_RESULTS,
  ];
  let showQuestion =
    gameStatus.currentState === GAMESTATE_ROUND &&
    questionVisibleState.includes(gameStatus.roundState);

  let showDD =
    gameStatus.currentState === GAMESTATE_ROUND &&
    gameStatus.dailyDouble;

  let modal = <div />;

  if (showQuestion || showDD) {
    modal = <GameBoardModal />;
  }

  const onEndRound = async () => {
    const { round } = gameStatus;
    const nextState = {
      round,
      currentState: "lobby",
    };

    if (round === Constants.GAMESTATE_ROUND_PRACTICE) {
      await Provider.resetScores(gameId);
    }

    dispatch(updateGameStateAction(gameId, nextState));
  };

  let fab = (
    <StyledFab
      variant="extended"
      color="primary"
      size="large"
      onClick={onEndRound}
    >
      <FabIcon />
      End Round
    </StyledFab>
  );
  if (gameStatus.round === GAMESTATE_ROUND_FINAL) {
    fab = null;
  }

  const isFinalRound = gameStatus.round === GAMESTATE_ROUND_FINAL;

  return (
    <Root
      sx={
        isFinalRound
          ? {
              minHeight: "100vh",
              background:
                "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
            }
          : undefined
      }
    >
      <Helmet>
        <meta charSet="utf-8" />
        <title>Groupardy - Game Board</title>
      </Helmet>
      {!isFinalRound && <BackgroundEffect />}
      <Box display="flex" height="100vh">
        <Box flex={isFinalRound ? 1 : 10} display="flex" flexDirection="column" minWidth={0}>
          {mainBoard}
        </Box>
        {!isFinalRound && (
          <Box width="280px" flexShrink={0} position="relative">
            <GameboardLeaderboard />
          </Box>
        )}
      </Box>
      {modal}
      {fab}
    </Root>
  );
};

export default GameBoardState;
